import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { ASSET_POLL_INTERVAL_MS } from '@/hooks/useDataPolling/pollingConstants';
import { api, getBaseUrl } from '@/services/api';
import { AssetState } from '@/api/generatedApi';
import { isLabelPending } from '@/utils/assets';
import { Box, CircularProgress, styled } from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { AssetSidebar } from '../AssetSidebar';
import { DependencyDetailPopover } from './DependencyDetailPopover';
import { AssetErrorMessage } from './AssetErrorMessage';
import { FailedAssetMessage } from './FailedAssetMessage';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { useCriticalGeometryPut } from '@/hooks/assetViewer/useCriticalGeometryPut';
import { useSelectionSync } from '@/hooks/assetViewer/useSelectionSync';
import { useThemeMode } from '@/hooks/useThemeMode';
import { DependencyContext } from '../AssetSidebar/DependencyTree/DependencyTreeView';

const MainContentArea = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  overflow: 'hidden',
});

const AssetView = styled(Box)(({ theme }) => ({
  flexDirection: 'column',
  flex: '2 1 auto',
  minWidth: '200px',
  display: 'flex',
  backgroundColor: theme.palette.background.default,
}));

const AssetIframe = styled('iframe')({
  width: '100%',
  height: '100%',
  border: 0,
});

const ZeroStateBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
});

type AssetViewerState = 'unsupported' | 'indexing' | 'no-3d-data' | 'failed' | 'viewer';

const getAssetViewerState = (assetState?: AssetState): AssetViewerState => {
  if (assetState === AssetState.Unsupported) {
    return 'unsupported';
  }

  if (assetState === AssetState.Failed) {
    return 'failed';
  }

  if (assetState === AssetState.Indexing) {
    return 'indexing';
  }

  if (assetState === AssetState.No3DData) {
    return 'no-3d-data';
  }

  return 'viewer';
};

export const AssetViewer = ({ assetId }: { assetId?: string }) => {
  const { selectedTenantId: assetTenantId } = useSearchFilters();
  const { resolvedMode } = useThemeMode();

  const {
    asset,
    error: assetError,
    isLoading: assetIsLoading,
  } = useGetAsset({
    assetId: assetId ?? '',
    assetTenantId,
    refetchInterval: query => {
      const fetchedAsset = query.state.data?.data.asset;
      if (fetchedAsset?.state === AssetState.Indexing || isLabelPending(fetchedAsset)) return ASSET_POLL_INTERVAL_MS;
      return false;
    },
  });
  const [error, setError] = useState<string>();
  const iframeReference = useRef<HTMLIFrameElement>(null);
  const viewerRequestReference = useRef(0);
  const contentReference = useRef<HTMLDivElement>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [selectedSelectionPath, setSelectedSelectionPath] = useState<string[]>([]);
  const [selectedDependencyContext, setSelectedDependencyContext] = useState<DependencyContext>();
  const { put: handleReprocessCriticalGeometry, isPutInFlight: isReprocessInFlight } = useCriticalGeometryPut({
    assetId,
    tenantId: assetTenantId,
    iframeRef: iframeReference,
    assetType: asset?.type,
  });

  const viewState = getAssetViewerState(asset?.state);

  const handlePopoverClose = () => {
    setSelectedAssetIds([]);
    setSelectedSelectionPath([]);
    setSelectedDependencyContext(undefined);
  };

  const { sendSelectionToViewer, sendClearToViewer } = useSelectionSync({
    iframeRef: iframeReference,
    onViewerSelect: (assetIds: string[], selectionPath?: string[]) => {
      if (assetIds.length === 0) return;
      setSelectedAssetIds(assetIds);
      setSelectedSelectionPath(selectionPath ?? []);
      setSelectedDependencyContext(undefined);
    },
    onViewerClear: () => {
      handlePopoverClose();
    },
  });

  const handleTreeItemClick = (itemId: string, context?: DependencyContext, selectionPath?: string[]) => {
    setSelectedAssetIds(itemId ? [itemId] : []);
    setSelectedSelectionPath(selectionPath ?? []);
    setSelectedDependencyContext(context);
    if (itemId) {
      sendSelectionToViewer([itemId], selectionPath);
    } else {
      sendClearToViewer();
    }
  };

  const fetchViewer = async (requestId: number) => {
    setError(undefined);
    try {
      const apiLocation = getBaseUrl();

      const {
        data: { token },
      } = await api.viewer.viewerToken();

      if (requestId !== viewerRequestReference.current) {
        return;
      }

      const iframeSource = `${apiLocation}/tenants/${assetTenantId}/viewer/asset?token=${token}&assetId=${assetId}&theme=${resolvedMode}&parentOrigin=${encodeURIComponent(globalThis.location.origin)}`;

      if (iframeReference.current) {
        iframeReference.current.src = iframeSource;
      }
    } catch (error) {
      if (requestId !== viewerRequestReference.current) {
        return;
      }

      setError(error instanceof AxiosError ? error.message : 'Error fetching viewer');
    }
  };

  useEffect(() => {
    // The token outlives the asset it was requested for, so a slow response for one asset
    // must not be applied to the frame now showing another. Counting here rather than inside
    // the fetch retires an in-flight request even when the new state asks for no fetch at all.
    viewerRequestReference.current += 1;

    if (assetId && !assetError && !assetIsLoading && viewState === 'viewer') {
      void fetchViewer(viewerRequestReference.current);
    }
  }, [assetId, assetTenantId, assetError, assetIsLoading, viewState]);

  useEffect(() => {
    const iframe = iframeReference.current;
    if (!iframe?.contentWindow || !iframe.src) return;
    const targetOrigin = new URL(iframe.src).origin;
    iframe.contentWindow.postMessage({ type: 'physna:theme-mode', mode: resolvedMode }, targetOrigin);
  }, [resolvedMode]);

  if (assetIsLoading) {
    return (
      <ZeroStateBox>
        <CircularProgress />
      </ZeroStateBox>
    );
  }

  if (assetError) {
    return <AssetErrorMessage errorMessage={assetError.message} />;
  }

  return (
    <MainContentArea>
      <AssetSidebar
        asset={asset}
        onTreeItemClick={handleTreeItemClick}
        onReprocessCriticalGeometry={handleReprocessCriticalGeometry}
        isReprocessCriticalGeometryDisabled={!assetId || !assetTenantId || isReprocessInFlight}
        selectedSelectionPath={selectedSelectionPath}
      />
      <AssetView ref={contentReference}>
        {viewState === 'unsupported' && <AssetErrorMessage assetState={AssetState.Unsupported} />}
        {viewState === 'failed' && <FailedAssetMessage assetId={assetId} />}
        {viewState === 'indexing' && <AssetErrorMessage assetState={AssetState.Indexing} />}
        {viewState === 'no-3d-data' && <AssetErrorMessage assetState={AssetState.No3DData} />}
        {viewState === 'viewer' && (
          <>
            <AssetIframe
              key={assetId}
              ref={iframeReference}
              title='3D Model Viewer'
              sandbox='allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox'
              referrerPolicy='no-referrer'
            />
            {error && <div>{error}</div>}
          </>
        )}
        <DependencyDetailPopover
          open={selectedAssetIds.length > 0}
          anchorEl={contentReference.current}
          selectedTreeItemId={selectedAssetIds.at(-1) ?? ''}
          assetTenantId={asset?.tenantId}
          dependencyContext={selectedDependencyContext}
          onClose={handlePopoverClose}
        />
      </AssetView>
    </MainContentArea>
  );
};
